import { UserNotificationType } from '@/types/notification.type'

export function getNotificationDetailHref(id: number): string {
  return `/notifications/${id}`
}

/** Admin broadcast / announcement notifications always open the detail view. */
export function isAnnouncementNotification(notification: UserNotificationType): boolean {
  if (notification.notificationType === 'admin-broadcast') return true
  const metadata = notification.metadata
  if (!metadata) return false
  return Boolean(metadata.adminUserId || metadata.targetType)
}

/** Validates optional notification destination: empty, site path (/...), or http(s) URL. */
export function isValidNotificationDestinationUrl(value: string | undefined | null): boolean {
  const trimmed = value?.trim()
  if (!trimmed) return true

  if (trimmed.startsWith('//')) return false

  if (trimmed.startsWith('/')) {
    try {
      new URL(trimmed, 'http://localhost')
      return true
    } catch {
      return false
    }
  }

  if (!/^https?:\/\//i.test(trimmed)) return false

  try {
    const url = new URL(trimmed)
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch {
    return false
  }
}

/** True when notification should open the in-app detail page instead of an external path. */
export function hasAttachedNotificationUrl(destinationUrl: string | undefined | null): boolean {
  const trimmed = destinationUrl?.trim()
  if (!trimmed) return false

  let pathname: string
  try {
    pathname = trimmed.startsWith('http') ? new URL(trimmed).pathname : trimmed.split('?')[0]
  } catch {
    return false
  }

  const normalized = pathname.replace(/\/+$/, '') || '/'
  if (normalized === '/notifications') return false
  if (/^\/notifications\/\d+$/.test(normalized)) return false

  return true
}

/**
 * Link shown inside announcement detail (custom destination), not the detail route itself.
 * Prefers metadata.link when present; otherwise uses destinationUrl when it is a real attach URL.
 */
export function getAnnouncementActionLink(notification: UserNotificationType): string | null {
  const metadataLink = notification.metadata?.link
  if (typeof metadataLink === 'string' && hasAttachedNotificationUrl(metadataLink)) {
    return metadataLink.trim()
  }
  if (hasAttachedNotificationUrl(notification.destinationUrl)) {
    return notification.destinationUrl.trim()
  }
  return null
}

export type NotificationNavigationTarget =
  | { kind: 'detail'; href: string }
  | {
      kind: 'chat'
      conversationId: number
      messageId: number | null
    }
  | {
      kind: 'topic'
      href: string
    }
  | { kind: 'href'; href: string }

export function resolveNotificationNavigationTarget(
  notification: UserNotificationType
): NotificationNavigationTarget {
  if (isAnnouncementNotification(notification) || !hasAttachedNotificationUrl(notification.destinationUrl)) {
    return { kind: 'detail', href: getNotificationDetailHref(notification.id) }
  }

  const destinationUrl = notification.destinationUrl

  try {
    const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'http://localhost'
    const url = destinationUrl.startsWith('http') ? new URL(destinationUrl) : new URL(destinationUrl, baseUrl)
    const normalizedPath = url.pathname.replace(/\/+$/, '').toLowerCase()

    if (normalizedPath === '/chat') {
      const conversationId = url.searchParams.get('conversationId')
      const messageId = url.searchParams.get('messageId')
      if (conversationId) {
        return {
          kind: 'chat',
          conversationId: parseInt(conversationId, 10),
          messageId: messageId ? parseInt(messageId, 10) : null,
        }
      }
    }

    const topicUrlPattern = /\/courses\/([^/]+)\/topic\/(\d+)/
    const match = destinationUrl.match(topicUrlPattern)
    if (match?.[1] && match[2]) {
      const page = url.searchParams.get('page') || '1'
      return {
        kind: 'topic',
        href: `/my-courses/${match[1]}?topicId=${match[2]}&page=${page}`,
      }
    }
  } catch {
    // fall through to href navigation
  }

  return { kind: 'href', href: destinationUrl }
}
